const by = (states, trigger, reachable, cause) => {
    states.push(cause && !reachable ? { trigger, reachable, cause } : { trigger, reachable });
};
/**
 * @param items the EFFECTIVE items, roles already resolved through any matched branch.
 * @param triggerOf the targeted trigger an item would be named by.
 * @param ownsTrigger whether a trigger addresses one of these items at all — a state with any other
 *   trigger belongs to something else and is not this derivation's to judge.
 */
export function singlePickReachability(d, items, triggerOf, ownsTrigger) {
    const states = [];
    const correctItems = items.filter(o => o.role === 'correct');
    const wrongItems = items.filter(o => o.role !== 'correct');
    const graded = d.gradingMode === 'graded';
    /**
     * Targeted coverage, computed in EVERY mode — which it did not used to be.
     *
     * Survey and optional returned before this, back when they could not fire a targeted state at
     * all. Now they can (see `participationOutcome`), and the analysis has to follow or the editor
     * contradicts the grader in both directions at once: calling a state that now fires dead, and
     * calling a catch-all reachable that a full set of targeted states has closed off.
     *
     * The only mode-dependent part is WHY a state can be dead. In graded, one on a CORRECT item is
     * unreachable because correct returns before targeted runs. Outside graded nothing is correct,
     * so every item's state can fire.
     */
    const itemByTrigger = new Map(items.map(o => [triggerOf(o), o]));
    const covered = new Set();
    for (const s of d.feedback.states) {
        if (s.type !== 'targeted' || !ownsTrigger(s.trigger))
            continue;
        const item = itemByTrigger.get(s.trigger);
        const ok = !!item && (!graded || item.role !== 'correct');
        by(states, s.trigger, ok, ok ? undefined : 'no-correct-option');
        if (ok)
            covered.add(s.trigger);
    }
    if (d.gradingMode === 'optional') {
        /**
         * OPTIONAL is INERT — see `targetedFiresIn`. The catch-all always fires and nothing else can:
         * no targeted state, no gate, no skip state of its own. It used to enumerate a blank against
         * the options and close the catch-all when they covered everything, which is gone with the
         * targeting that justified it.
         */
        by(states, 'any_response', true);
        for (const s of d.feedback.states) {
            if (s.trigger !== 'any_response')
                by(states, s.trigger, false, 'mode');
        }
        // `uncoveredWrong` is "what the catch-all still catches": every item, since nothing targets one.
        return { states, correctItems: [], uncoveredWrong: items };
    }
    // The empty check runs before the survey branch, so unanswered survives in both modes.
    by(states, 'unanswered', true);
    if (d.gradingMode === 'survey') {
        /**
         * The catch-all closes when every item is covered — the SAME rule graded applies to Default
         * Incorrect below, because `any_answer` plays the same role.
         *
         * Every item, not just the wrong ones: nothing is correct here, so nothing is terminal and
         * every pick can reach a targeted state. A blank cannot reach this branch at all, having been
         * caught as unanswered above, so a fully covered list genuinely leaves it nothing.
         */
        const uncovered = items.filter(o => !covered.has(triggerOf(o)));
        const reachable = items.length > 0 && uncovered.length > 0;
        by(states, 'any_answer', reachable, reachable ? undefined : items.length === 0 ? 'nothing-to-pick' : 'fully-covered');
        for (const t of ['correct', 'incorrect', 'any_response'])
            by(states, t, false, 'mode');
        // See the optional branch: the catch-all's own list, which is what the panel draws.
        return { states, correctItems: [], uncoveredWrong: uncovered };
    }
    by(states, 'any_answer', false, 'mode');
    by(states, 'any_response', false, 'mode');
    // One pick, and it is correct if its own role says so — no conjunction, so ANY correct item makes
    // the state reachable and several make it easier rather than impossible.
    by(states, 'correct', correctItems.length > 0, correctItems.length > 0 ? undefined : 'no-correct-option');
    // Closed list, so this genuinely CAN be covered off — the difference from Keyword and FITB, whose
    // free text always leaves something for the catch-all.
    const uncoveredWrong = wrongItems.filter(o => !covered.has(triggerOf(o)));
    by(states, 'incorrect', uncoveredWrong.length > 0, uncoveredWrong.length > 0 ? undefined : 'fully-covered');
    return { states, correctItems, uncoveredWrong };
}
//# sourceMappingURL=single-pick-reachability.js.map